import { useApolloPaging } from 'hooks';
import useEmployeeNameFormatter from 'hooks/ui/useEmployeeNameFormatter';
import { t } from 'i18next';
import { capitalize, isNil } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useRef } from 'react';
import ICursorable from 'types/Cursorable';
import IPhotoVerificationReportRowItem from 'types/PhotoVerificationReportRowItem';
import ITimeEntryRekognition from 'types/TimeEntryRekognition';
import ITimeRange from 'types/TimeRange';
import TimeRangeType from 'types/TimeRangeType';
import PhotoType from 'types/enum/PhotoType';
import { dateTimeFromISOKeepZone } from 'utils/dateUtils';
import { timeEntryRekognitionQuery } from '../photo-verification-queries';

export interface IPhotoVerificationFilter {
  memberId: string | null;
  currentTimeRangeType: TimeRangeType | null | undefined;
  timeRange: ITimeRange<DateTime | null> | null | undefined;
  photoType: PhotoType;
}

export interface IPhotoVerificationData {
  data: IPhotoVerificationReportRowItem[];
  failedMatches: number;
}

export default function usePhotoVerificationReportData(filter: IPhotoVerificationFilter) {
  const { getAll } = useApolloPaging();
  const nameFormatter = useEmployeeNameFormatter();
  const cachedTimeRangeType = useRef<TimeRangeType | null | undefined>();
  const cachedTimeRange = useRef<ITimeRange<DateTime | null> | null | undefined>();

  return useCallback(async () => {
    if (
      !isNil(cachedTimeRangeType.current) &&
      filter.currentTimeRangeType !== cachedTimeRangeType.current &&
      (filter.timeRange?.startTime?.toSeconds() === cachedTimeRange.current?.startTime?.toSeconds() ||
        filter.timeRange?.endTime?.toSeconds() === cachedTimeRange.current?.endTime?.toSeconds())
    ) {
      return { data: [], failedMatches: 0 } as IPhotoVerificationData;
    }
    cachedTimeRangeType.current = filter.currentTimeRangeType;
    cachedTimeRange.current = filter.timeRange;
    const photoVerificationData = getAll<ITimeEntryRekognition & ICursorable>('timeEntryRekognition', {
      query: timeEntryRekognitionQuery,
      variables: {
        filter: {
          deletedOn: { isNull: true },
          createdOn: {
            between: {
              start: filter.timeRange?.startTime
                ?.minus(filter.timeRange?.startTime.toLocal().offset * 60 * 1000)
                .toISO({ suppressSeconds: true }),
              end: filter.timeRange?.endTime
                ?.minus(filter.timeRange?.endTime.toLocal().offset * 60 * 1000)
                .toISO({ suppressSeconds: true }),
            },
          },
          timeEntry: {
            member: { permissions: { permissions: 'timeEvents', operationType: 'and' } },
          },
        },
      },
      fetchPolicy: 'network-only',
    });

    return await Promise.all([photoVerificationData]).then((queryResults) => {
      const [res] = queryResults;
      let failedMatches = 0;
      const kioskData = res.map((timeEntryRekognition: ITimeEntryRekognition) => timeEntryRekognition);

      const relevantData = kioskData.filter((item: ITimeEntryRekognition) => {
        return item.similarity !== null;
      });

      const data: IPhotoVerificationReportRowItem[] = relevantData.map(
        (item: ITimeEntryRekognition): IPhotoVerificationReportRowItem => {
          const action = item.type
            .split('-')
            .map((value) => capitalize(value))
            .join(' ');

          return {
            memberName: nameFormatter(item.timeEntry.member.firstName ?? '', item.timeEntry.member.lastName ?? ''),
            memberId: item.timeEntry.member.id,
            match: item.similarity >= 90,
            comparison: {
              actionImageUrl: item.imageUrl,
              profileImageUrl: item.referenceImageUrl,
            },
            accuracy: item.similarity || 0,
            action,
            date: dateTimeFromISOKeepZone(item.createdOnLocal),
            time: dateTimeFromISOKeepZone(item.createdOnLocal).toFormat('t'),
            timeDst: item.createdOnDst,
            timeEntryDeleted: item.timeEntry.deletedOn ? true : false,
            timeEntryId: item.timeEntry.id,
            source: !isNil(item.source) ? (item.source === 'Clock' ? t('Employee Clock') : t('Kiosk')) : t('Kiosk'),
            createdOn: item.createdOn,
          };
        }
      );

      const filteredData = data.filter((item) => {
        const memberId = filter.memberId;
        const photoType = filter.photoType;
        if (!item.timeEntryDeleted) {
          if (memberId && !photoType) {
            return item.memberId === memberId;
          } else if (photoType && !memberId) {
            if (photoType === PhotoType.ALL) {
              return item;
            }
            if (photoType === PhotoType.FAILED) {
              return item.match === false;
            }
            if (photoType === PhotoType.MATCHING) {
              return item.match === true;
            }
          } else if (photoType && memberId) {
            return item.memberId === memberId;
          } else {
            return item;
          }
        }
      });

      filteredData.map((item) => {
        if (item.accuracy < 90) {
          failedMatches += 1;
        }
      });

      return { data: filteredData, failedMatches } as IPhotoVerificationData;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filter.memberId,
    filter.photoType,
    filter.timeRange?.endTime,
    filter.timeRange?.startTime,
    filter.currentTimeRangeType,
    getAll,
    nameFormatter,
  ]);
}
